import { TFunction } from 'i18next';
import { UseFormSetError } from 'react-hook-form';
import {
  DynamicFormValues,
  PhotoFormType,
} from '@/components/DefectDetailModal/DefectDetails';
import {
  Defect,
  DefectRequest,
  Dimension,
  PHOTO_TYPE,
  REQUIRED_FIELD,
  SubmittedDefect,
  DimensionRequest,
  SortDynamicFieldType,
  FormDimension,
} from '@/types/interfaces/defect-details';
import { FormSelectOption } from '@/types/interfaces/form';
import { LENGTH_FORMAT, parseDecimal, toLengthUnit } from './unit';

export const dynamicFormDefaultValues: DynamicFormValues = {
  defect: null,
  comment: '',
  defectImportance: null,
  defectQuantity: null,
  defectSize: null,
  color: null,
  categoryBrandModel: null,
  sizeType: null,
  size: null,
  material1: null,
  material1Percent: '',
  material2: null,
  material2Percent: '',
  weight: '',
  dimensions: [],
  isAppearedInPhoto: false,
  isFromMeasurementAssistant: false,
  category: null,
  subcategory: null,
  brand: '',
};

export const defectFieldMap: Map<REQUIRED_FIELD, (keyof Defect)[]> = new Map([
  [
    REQUIRED_FIELD.IMPORTANCE,
    ['defectImportances', 'defectSizes', 'defectQuantities'],
  ],
  [REQUIRED_FIELD.COLOR, ['colors']],
  [REQUIRED_FIELD.MATERIAL, ['materials']],
  [REQUIRED_FIELD.SIZE, ['sizeTypes']],
  [REQUIRED_FIELD.MODEL, ['categoryBrandModels']],
  [
    REQUIRED_FIELD.CATEGORY,
    ['categories', 'subcategories', 'categorySizeTypes'],
  ],
  [REQUIRED_FIELD.BRAND, ['brand']],
]);

export const INCORRECT_MEASUREMENT_DEFECT_TYPE_ID = 243;

export const defectSubmissionMap = (
  data: DynamicFormValues & { photos: PhotoFormType[] }
) => {
  const dimensions = data.dimensions?.length
    ? data.dimensions.filter((d) => !!d && d.value)
    : undefined;

  const result: DefectRequest = {
    defectTypeId: data.defect?.id || 0,
    photos: data.photos.length
      ? data.photos
          .filter((p) => !!p && p.checked && !p.isHidden)
          .map((p) => {
            return {
              path: p.path || '',
              annotations:
                p.annotations?.map((a) => ({ x: a.x / 100, y: a.y / 100 })) ||
                [],
              type: p.type === 'produit' ? 'original' : 'uploaded',
            };
          })
      : undefined,
    isAppearedInPhoto: data.isAppearedInPhoto,
    internalComment: data.comment?.trim(),
    defectImportance: data.defectImportance?.value,
    defectQuantity: data.defectQuantity?.value,
    defectSize: data.defectSize?.value,
    category: parseInt(data.category?.value || '') || undefined,
    subcategory: parseInt(data.subcategory?.value || '') || undefined,
    size: data.size?.value,
    sizeType: data.sizeType?.value,
    weight: data.weight ? parseDecimal(data.weight) : undefined,
    color: data.color?.value ? parseInt(data.color.value) : undefined,
    categoryBrandModel: data.categoryBrandModel?.value
      ? parseInt(data.categoryBrandModel.value)
      : undefined,
    material1: data.material1?.value
      ? parseInt(data.material1.value)
      : undefined,
    material1Percent: data.material1Percent
      ? parseDecimal(data.material1Percent)
      : undefined,
    material2: data.material2?.value
      ? parseInt(data.material2.value)
      : undefined,
    material2Percent: data.material2Percent
      ? parseDecimal(data.material2Percent)
      : undefined,
    dimensions: dimensions as DimensionRequest,
    isFromMeasurementAssistant:
      data.isFromMeasurementAssistant &&
      INCORRECT_MEASUREMENT_DEFECT_TYPE_ID === data.defect?.id,
    brand: data.brand,
  };

  Object.keys(result).forEach(
    (key) =>
      result[key as keyof DefectRequest] === undefined &&
      delete result[key as keyof DefectRequest]
  );

  return result;
};

export const checkHavingDimension = (
  setError: UseFormSetError<DynamicFormValues>,
  t: TFunction<'translation', undefined>,
  dimensions: FormDimension,
  requiredFields?: REQUIRED_FIELD[]
) => {
  const isRequireDimensions = requiredFields?.some(
    (f) => f === REQUIRED_FIELD.DIMENSION
  );

  if (!isRequireDimensions) return true;

  const hasDimension = dimensions.some((d) => !!d?.id && !!d.value);

  if (!hasDimension) {
    setError('dimensions', {
      message: t('QC_V2.DEFECT.DIMENSION_ONE_INPUT_VAL'),
    });
    return false;
  }

  return true;
};

export const validateDefectPhotos = (
  setError: UseFormSetError<DynamicFormValues>,
  photos: PhotoFormType[],
  t: TFunction<'translation', undefined>,
  requiredFields?: REQUIRED_FIELD[]
) => {
  if (!requiredFields?.length) return true;

  const allPhotos = photos.filter((p) => !!p && p.checked);

  const isPhotoRequired = requiredFields?.includes(REQUIRED_FIELD.PICTURE);

  const hasUploadedPics =
    allPhotos.findIndex((p) => p.type === PHOTO_TYPE.NPV) > -1;

  if (isPhotoRequired && !hasUploadedPics) {
    setError('pdpPhotos', { message: t('QC_V2.DEFECT.UPLOAD_PHOTO_REQUIRED') });
    return false;
  }

  const isAnnotationRequired = requiredFields?.includes(
    REQUIRED_FIELD.LOCATION
  );

  const hasAnnotation =
    allPhotos.findIndex((p) => !!p.annotations?.length) > -1;

  if (isAnnotationRequired && !hasAnnotation) {
    setError('pdpPhotos', { message: t('QC_V2.DEFECT.ANNOTATION_REQUIRED') });
    return false;
  }

  return true;
};

export const defectDefaultValueMap = ({
  data,
  dimensions,
  isPageView,
  pdpImages,
}: {
  data: SubmittedDefect;
  isPageView: boolean;
  pdpImages: string[];
  dimensions?: Dimension[];
}): DynamicFormValues => {
  let produitPhotos: PhotoFormType[] = [];
  let npvPhotos: PhotoFormType[] = [];

  if (data.photos?.length) {
    const photos = data.photos.map((p, idx) => {
      const annotations = p.annotations.map((a) => ({
        x: a.x * 100,
        y: a.y * 100,
      }));
      return { ...p, annotations, id: idx };
    });

    // Page-view (from QC list) and QC-ing view mode UI is different
    // Page-view shows all photos so don't separate NPV and PRODUIT section. Id will start from 0 to length-1
    // QC-ing view mode is separate NPV and PRODUIT. Id for each section will start from 0
    const isRepopulateId = !isPageView;

    produitPhotos = photos
      .filter((p) => p.type === 'original')
      .map((p, idx) => {
        const index = pdpImages?.findIndex(
          (path) => `${PHOTO_TYPE.PRODUIT}/${path}` === p.path
        );
        const id = index > -1 ? index : idx;

        return {
          ...p,
          id: isRepopulateId ? id : p.id,
          type: PHOTO_TYPE.PRODUIT,
          checked: true,
        };
      });

    npvPhotos = photos
      .filter((p) => p.type === 'uploaded')
      .map((p, idx) => ({
        ...p,
        id: isRepopulateId ? idx : p.id,
        type: PHOTO_TYPE.NPV,
        checked: true,
      }));
  }

  const bucket = data.defectType?.bucket;

  const lengthUnit = toLengthUnit();

  const mappedDimensions =
    dimensions && dimensions.length
      ? dimensions.map((d) => {
          const item = data.dimensions?.find(
            (i) => i.typeDimension?.id === d.id
          );
          if (!item) return undefined;

          const value =
            lengthUnit === LENGTH_FORMAT.CM.toLowerCase()
              ? item.value
              : item.valueInch;

          return { id: d.id, value: value || '' };
        })
      : [];

  const result: DynamicFormValues = {
    defect:
      data.defectType && bucket
        ? {
            label: data.defectType.name,
            id: data.defectType.id,
            forceKo: data.defectType.forceKo,
            bucketId: bucket.id,
            bucketName: bucket.name,
            requiredFields: bucket?.requiredFields,
            optionalFields: bucket?.optionalFields,
          }
        : undefined,
    comment: data.internalComment,

    defectImportance: data.defectImportance
      ? {
          label: data.defectImportance.label,
          value: data.defectImportance.value,
        }
      : undefined,
    defectQuantity: data.defectQuantity
      ? {
          label: data.defectQuantity.label,
          value: data.defectQuantity.value,
        }
      : undefined,
    defectSize: data.defectSize
      ? {
          label: data.defectSize.label,
          value: data.defectSize.value,
        }
      : undefined,

    color: data.color
      ? { label: data.color.name, value: data.color.id.toString() }
      : undefined,
    categoryBrandModel: data.categoryBrandModel
      ? {
          label: data.categoryBrandModel.name,
          value: data.categoryBrandModel.id.toString(),
        }
      : undefined,

    category: data.category
      ? {
          label: data.category.completeName,
          value: data.category.id.toString(),
          legacyId: data.category.legacyId.toString(),
        }
      : undefined,
    subcategory: data.subcategory
      ? {
          label: data.subcategory.name,
          value: data.subcategory.id.toString(),
          legacyId: data.subcategory.legacyId.toString(),
        }
      : undefined,

    sizeType: data.sizeType
      ? { label: data.sizeType, value: data.sizeType }
      : undefined,
    size: data.size ? { label: data.size, value: data.size } : undefined,

    material1: data.material1
      ? { label: data.material1.name, value: data.material1.id.toString() }
      : undefined,
    material1Percent: data.material1Percent?.toString(),

    material2: data.material2
      ? { label: data.material2.name, value: data.material2.id.toString() }
      : undefined,
    material2Percent: data.material2Percent?.toString(),

    weight: data.weight?.toString(),

    dimensions: mappedDimensions,

    pdpPhotos: produitPhotos,
    uploadedPhotos: npvPhotos,

    isAppearedInPhoto: data.isAppearedInPhoto,

    brand: data.brand,
  };

  Object.keys(result).forEach(
    (key) =>
      result[key as keyof DynamicFormValues] === undefined &&
      delete result[key as keyof DynamicFormValues]
  );

  return result;
};

export const checkRequired = (
  required: string | undefined,
  options: FormSelectOption[] | string[]
) => {
  if (!options.length) return undefined;
  return required;
};

const ORDERED_FIELDS = [
  REQUIRED_FIELD.MODEL,
  REQUIRED_FIELD.COLOR,
  REQUIRED_FIELD.WEIGHT,
  REQUIRED_FIELD.MATERIAL,
  REQUIRED_FIELD.CATEGORY,
  REQUIRED_FIELD.SIZE,
  REQUIRED_FIELD.DIMENSION,
  REQUIRED_FIELD.IMPORTANCE,
  REQUIRED_FIELD.PICTURE,
  REQUIRED_FIELD.LOCATION,
];

export const getDynamicFields = (
  respondedFields: REQUIRED_FIELD[],
  mappingFields: Record<REQUIRED_FIELD, JSX.Element>
) => {
  return respondedFields.reduce((acc, current) => {
    const el = mappingFields[current];
    if (el) acc.push({ type: current, element: el });
    return acc;
  }, [] as SortDynamicFieldType);
};

export const toSortedDynamicFields = (fields: SortDynamicFieldType) =>
  fields
    .sort(
      (a, b) => ORDERED_FIELDS.indexOf(a.type) - ORDERED_FIELDS.indexOf(b.type)
    )
    .map((f) => f.element);
